import React, {Fragment} from 'react';

import {InferProps} from 'prop-types';
import type {WithStyles} from '@material-ui/core';
import {Drawer, Grid, withStyles} from '@material-ui/core';

import Menu from './menu/Menu';
import {useFlowUpdate} from 'hooks';
import PageNav from './page-nav/PageNav';
import logo from 'assets/img/logo-white.svg';
import {ModuleItem} from 'commons/types/Types';
import {findActivePath} from 'commons/utils/Functions';
import {SidebarStyles} from 'assets/tss/layouts/admin';
import {useUserContext} from 'context/user/UserContext';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useSnackbar} from 'notistack';
import {HistoryRoutes, Messages, SnackbarVariants} from 'commons/constants';
import {useHistory} from 'react-router-dom';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import Topbar from '../topbar/Topbar';

function Sidebar(props: InferProps<WithStyles<typeof SidebarStyles>>) {
    const {classes} = props;
    const history = useHistory();
    const {menu, layout, fatherKycId, setKycId} = useMenuContext();
    const {user} = useUserContext();
    const {
        setTempRealOwnerActive,
        setSelectedRepresentative,
        setTempShareholder,
        setTempShaholderActive,
        setEditLocation,
    } = useRelatedPeopleContext();
    const {enqueueSnackbar} = useSnackbar();
    const {flow} = useFlowContext();
    const [steps, setSteps] = React.useState<number>(0);
    const showBgImg = !user.loggedIn || ['/login', '/recover-password'].includes(flow.data.path);

    React.useEffect(() => {
        if (menu) {
            setSteps(menu.filter((item: ModuleItem) => item.showLinearProgress).length);
        }
    }, [menu]);

    function changeView(path: string) {
        if (layout.views && layout.views.length && fatherKycId !== 0 && path && path.length) {
            resetContext(path);
            history.push(path);
        } else {
            enqueueSnackbar(Messages.MENU_PERSON_TYPE_UNKNOWN_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function resetContext(path: string) {
        const legalRepresentativesRoutes: string[] = [
            HistoryRoutes.REPRESENTATIVE_ADDITIONAL,
            HistoryRoutes.REPRESENTATIVE_OFFICIAL_ID,
            HistoryRoutes.REPRESENTATIVE_OFFICIAL_INFO,
            HistoryRoutes.REPRESENTATIVE_PERSONAL,
            HistoryRoutes.REPRESENTATIVE_POWER,
            HistoryRoutes.REPRESENTATIVE_SIGN,
        ];
        const shareholdersRoutes: string[] = [
            HistoryRoutes.SHAREHOLDER_INFO,
            HistoryRoutes.SHAREHOLDER_LEGAL_REPRESENTATIVE,
            HistoryRoutes.SHAREHOLDER_REPRESENTATIVE,
        ];
        if (path !== HistoryRoutes.REAL_OWNER_INFORMATION) {
            setTempRealOwnerActive(false);
            setEditLocation(false);
        }
        if (legalRepresentativesRoutes.indexOf(path) === -1) {
            setSelectedRepresentative(-1);
        }
        if (shareholdersRoutes.indexOf(path) === -1) {
            setKycId(-1);
            setTempShaholderActive(false);
            setTempShareholder(undefined);
        }
    }

    function onSubmit(item: ModuleItem, child: boolean) {
        try {
            if (child) {
                if (item) {
                    changeView(`${item.layout}${item.path}`);
                }
            } else {
                if (item.items && item.items.length) {
                    const child: ModuleItem | undefined = item.items.find(
                        (children: ModuleItem) => item.linkedChildId === children.viewId
                    );
                    if (child) {
                        changeView(`${child.layout}${child.path}`);
                    } else {
                        enqueueSnackbar(Messages.MENU_FATHER_ERROR, {
                            variant: SnackbarVariants.ERROR,
                        });
                    }
                } else {
                    changeView(`${item.layout}${item.path}`);
                }
            }
        } catch (error) {
            enqueueSnackbar(Messages.MENU_FATHER_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    return (
        <Fragment>
            {user.loggedIn && <Topbar />}
            <Drawer
                variant='permanent'
                anchor='left'
                className={classes.root}
                classes={{
                    paper: showBgImg ? classes.paper : classes.loggedIn,
                    paperAnchorDockedLeft: classes.docked,
                }}>
                <Grid
                    container
                    justifyContent='flex-start'
                    alignItems='flex-start'
                    className={showBgImg ? undefined : classes.menu}>
                    {useFlowUpdate(findActivePath(menu))}
                    {/* Logo */}
                    <Grid item xs={12}>
                        <img alt='logo' src={logo} height={16} />
                    </Grid>
                    {/* Main header */}
                    <Grid item xs={12}>
                        <PageNav
                            activePath={flow.data.title}
                            activeUser={user.username}
                            loggedIn={user.loggedIn}
                            currentStep={flow.data.index + 1}
                            totalSteps={steps}
                            showLinearProgress={flow.data.showLinearProgress}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                        {menu && menu.length > 0 && flow.data.showMenu && (
                            <Menu
                                menuItems={menu}
                                active={`${flow.data.viewId !== 0 ? flow.data.viewId : -1}`}
                                handleClick={onSubmit}
                            />
                        )}
                    </Grid>
                </Grid>
                {}
            </Drawer>
        </Fragment>
    );
}

export default withStyles(SidebarStyles)(Sidebar);
